import React from "react";
import {
  Building2, CalendarDays, Clock, GraduationCap, Mail, Phone, PhoneCall, Users,
} from "lucide-react";
import { AppButton, AppCard, AppCardContent, StatusBadge, UserAvatar } from "../../ui";
import { formatDate } from "../../../utils/format";

/** "Today" / "Yesterday" / "5 days ago" — how long since they were last seen. */
export const idleLabel = (days) => {
  if (days === null || days === undefined) return "";
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
};

/** One fact in the meta line: a glyph and a short phrase. */
const Fact = ({ icon: Icon, children }) => (
  <li className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1.5">
    <Icon size={14} className="tw-shrink-0 tw-text-content-subtle" aria-hidden="true" />
    <span className="tw-truncate">{children}</span>
  </li>
);

/** A way to reach the student — a link that does the reaching. */
const ContactLink = ({ icon: Icon, href, label, children }) => (
  <a
    href={href}
    aria-label={label}
    className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-rounded-sm tw-text-content tw-no-underline tw-focus-ring hover:tw-text-primary"
  >
    <Icon size={14} className="tw-shrink-0 tw-text-content-subtle" aria-hidden="true" />
    <span className="tw-truncate">{children}</span>
  </a>
);

/**
 * StudentIdentityCard — who this is, whether they can be reached, and the
 * record's actions.
 *
 * It knows nothing about permissions: the page decides which actions exist and
 * passes them in as `actions`, already built.
 *
 * Props:
 *   student      — the `GET /students/:id` student
 *   primary      — the primary enrolment (studentRecord.listEnrollments)
 *   otherCourses — how many other active enrolments there are
 *   accountState — studentRecord.accountState()
 *   activity     — studentRecord.engagement()
 *   live         — the enrolment in a live class right now, or null
 *   onCall       — click-to-call; omitted when telephony is off (then the
 *                  number is a plain tel: link)
 *   calling      — a click-to-call request is in flight
 *   actions      — the buttons and menu on the right
 */
const StudentIdentityCard = ({
  student, primary, otherCourses = 0, accountState, activity, live, onCall, calling = false, actions,
}) => {
  const branch = student.branchId?.name || student.batch?.branchId?.name;
  const guardianPhone = student.guardianMobile;

  return (
    <AppCard as="section" aria-label="Student summary">
      <AppCardContent className="tw-flex tw-flex-col tw-gap-4 min-[1200px]:tw-flex-row min-[1200px]:tw-items-start">
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-4">
          <UserAvatar name={student.name} src={student.profilePhoto || undefined} size="xl" />

          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <h2 className="tw-m-0 tw-min-w-0 tw-truncate tw-text-[1.375rem] tw-font-semibold tw-leading-tight tw-tracking-tight tw-text-content">
                {student.name}
              </h2>
              <StatusBadge status={student.status || "active"} domain="student" />
              {accountState && <StatusBadge status={accountState} domain="studentAccount" />}
              {live && <StatusBadge status="live" domain="batch">Live class now</StatusBadge>}
            </div>

            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-p-0 tw-text-[0.8125rem] tw-text-content-muted">
              {primary && (
                <Fact icon={GraduationCap}>
                  {primary.courseName}
                  {primary.batch?.name ? ` · ${primary.batch.name}` : " · No batch yet"}
                  {otherCourses > 0 && ` · +${otherCourses} more course${otherCourses > 1 ? "s" : ""}`}
                </Fact>
              )}
              {branch && <Fact icon={Building2}>{branch}</Fact>}
              <Fact icon={CalendarDays}>Joined {formatDate(student.joinedDate || student.createdAt)}</Fact>
              {activity?.hasLogin && (
                <Fact icon={Clock}>
                  {activity.lastActiveAt ? `Last active ${idleLabel(activity.idleDays)}` : "No recent activity"}
                </Fact>
              )}
            </ul>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2 tw-text-[0.8125rem]">
              {student.mobile && (
                <span className="tw-inline-flex tw-items-center tw-gap-2">
                  <ContactLink icon={Phone} href={`tel:${student.mobile}`} label={`Call ${student.name} on ${student.mobile}`}>
                    {student.mobile}
                  </ContactLink>
                  {onCall && (
                    <AppButton size="xs" variant="outline" onClick={onCall} disabled={calling}>
                      <PhoneCall size={14} /> {calling ? "Calling…" : "Call"}
                    </AppButton>
                  )}
                </span>
              )}
              {student.email && (
                <ContactLink icon={Mail} href={`mailto:${student.email}`} label={`Email ${student.name}`}>
                  {student.email}
                </ContactLink>
              )}
              {(student.guardianName || guardianPhone) && (
                guardianPhone ? (
                  <ContactLink
                    icon={Users}
                    href={`tel:${guardianPhone}`}
                    label={`Call guardian${student.guardianName ? ` ${student.guardianName}` : ""} on ${guardianPhone}`}
                  >
                    Guardian: {student.guardianName ? `${student.guardianName} · ` : ""}{guardianPhone}
                  </ContactLink>
                ) : (
                  <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-content-muted">
                    <Users size={14} className="tw-text-content-subtle" aria-hidden="true" />
                    Guardian: {student.guardianName}
                  </span>
                )
              )}
            </div>
          </div>
        </div>

        {actions && (
          <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-2">
            {actions}
          </div>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default StudentIdentityCard;
